import { useEffect, useRef, useState } from 'react';
import { Leaf, User, Mail, ArrowRight, Car, GraduationCap, Phone, Shield, ChevronLeft, ChevronRight, FileText, Users, Route, ShieldCheck, MapPin, Wallet, Clock3 } from 'lucide-react';
import { UserRole, RegistrationExtraData } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface OnboardingFlowProps {
  onComplete: (name: string, role: UserRole, email: string, extraData: RegistrationExtraData) => void;
  onLoginClick: () => void;
}

type OnboardingStep = 'role-select' | 'details-form' | 'submitting';

const termsPages = [
  {
    title: '1. Platform Purpose',
    body: [
      'CollegeRyde presently operates as a digital bulletin board intended to help verified college students connect with fellow students regarding prospective ride opportunities.',
      'At this time, CollegeRyde does not provide transportation services, employ drivers, own or maintain vehicles, or guarantee that any posted ride will be completed.',
      'Users acknowledge that the platform is offered to support campus ride coordination, and that the nature, scope, and structure of the service may be revised as the platform develops.',
    ],
  },
  {
    title: '2. Eligibility and Verification',
    body: [
      'Access to the platform is limited to verified students associated with a participating campus community.',
      'Each user is responsible for providing accurate registration details and for keeping student, contact, and account information current.',
      'CollegeRyde reserves the right to restrict, suspend, or terminate access where a user appears to have misrepresented identity, campus affiliation, eligibility, or platform role.',
    ],
  },
  {
    title: '3. Safety, Conduct, and Community Values',
    body: [
      'CollegeRyde supports community, togetherness, and diversity, and all users are expected to conduct themselves with respect, care, and sound judgment.',
      'Students should exercise caution at all times when arranging rides, selecting pickup or drop-off locations, sharing personal information, or entering another student’s vehicle.',
      'Each user remains individually responsible for determining whether a proposed ride arrangement is appropriate, safe, and suitable before agreeing to participate.',
    ],
  },
  {
    title: '4. Fares, Insurance, and Incidents',
    body: [
      'Student drivers may request only fare amounts that reasonably correspond to the distance traveled and the ride being provided to fellow students.',
      'Each student is solely responsible for maintaining all legally required insurance coverage, driver licensing, vehicle registration, and the general roadworthiness of any vehicle used.',
      'In the event of any roadway incident, accident, injury, loss, damage, delay, or dispute arising from a ride arrangement, responsibility remains with the participating students and their respective insurance providers.',
    ],
  },
  {
    title: '5. Liability and Service Limits',
    body: [
      'CollegeRyde shall not be liable for events, injuries, losses, damages, disputes, or other issues arising while a user is not actively using the CollegeRyde service.',
      'As CollegeRyde remains a bulletin-board style platform, it is not a transportation carrier, transportation broker, insurer, emergency response provider, or guarantor of user conduct.',
      'By creating an account, users acknowledge that participation is voluntary, that operational and personal risk remains with the participating students, and that platform policies, procedures, and features may change in the future.',
    ],
  },
];

const onboardingHighlights = [
  {
    icon: Route,
    title: 'Mobility OS',
    description: 'A modular student mobility operating system built to support everyday transportation and urgent campus needs.',
  },
  {
    icon: Users,
    title: 'Peer Network',
    description: 'Designed around verified student connections so classmates can help one another move safely and reliably.',
  },
  {
    icon: ShieldCheck,
    title: 'Campus Trust',
    description: 'Structured around verification, safety guidance, and clear student responsibility from the start.',
  },
];

const onboardingStats = [
  { label: 'Verified access', value: 'Students only' },
  { label: 'Flexible use', value: 'Daily + urgent rides' },
  { label: 'Driver model', value: 'Distance-based fares' },
];

const OnboardingFlow = ({ onComplete, onLoginClick }: OnboardingFlowProps) => {
  const [step, setStep] = useState<OnboardingStep>('role-select');
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsPage, setTermsPage] = useState(0);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasReachedPageEnd, setHasReachedPageEnd] = useState(false);
  const termsContentRef = useRef<HTMLDivElement | null>(null);
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [driverLicense, setDriverLicense] = useState('');
  const [licensePlate, setLicensePlate] = useState('');

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setStep('details-form');
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!phoneNumber.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!hasAcceptedTerms) {
      toast.error('Please review and accept the CollegeRyde Policy and Guideline');
      return;
    }
    if (selectedRole === 'driver') {
      if (!driverLicense.trim()) {
        toast.error('Please enter your driver license number');
        return;
      }
      if (!licensePlate.trim()) {
        toast.error('Please enter your license plate');
        return;
      }
    }

    setStep('submitting');

    const extraData: RegistrationExtraData = {
      phone_number: phoneNumber,
      ...(selectedRole === 'driver' && {
        driver_license: driverLicense,
        license_plate: licensePlate,
      }),
    };

    onComplete(name, selectedRole!, email, extraData);
  };

  const currentTermsPage = termsPages[termsPage];
  const SelectedRoleIcon = selectedRole === 'driver' ? Car : GraduationCap;

  useEffect(() => {
    if (!isTermsOpen) return;

    const viewport = termsContentRef.current;
    if (!viewport) return;

    viewport.scrollTop = 0;
    const isAlreadyShort = viewport.scrollHeight <= viewport.clientHeight + 8;
    setHasReachedPageEnd(isAlreadyShort);
  }, [isTermsOpen, termsPage]);

  const handleTermsScroll = () => {
    const viewport = termsContentRef.current;
    if (!viewport) return;

    const reachedBottom = viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 8;
    if (reachedBottom && !hasReachedPageEnd) {
      setHasReachedPageEnd(true);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top_left,_hsla(25,85%,55%,0.18),_transparent_42%),radial-gradient(circle_at_top_right,_hsla(140,35%,45%,0.12),_transparent_36%)]" />
      <div className="absolute left-[-4rem] top-24 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute right-[-3rem] top-10 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10">
          <section className="flex flex-col justify-center">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/15 bg-card/80 px-4 py-2 text-sm font-medium text-foreground shadow-warm backdrop-blur">
              <div className="rounded-full bg-primary p-1.5">
                <Leaf className="h-4 w-4 text-primary-foreground" />
              </div>
              CollegeRyde
            </div>

            <div className="mt-6 max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Student Mobility Infrastructure</p>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-foreground sm:text-5xl">
                A modular student mobility operating system built for campus life.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
                CollegeRyde helps students access reliable peer-to-peer rides for daily essentials, urgent needs, and stronger community connection across campus.
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {onboardingStats.map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-border/70 bg-card/90 p-4 shadow-warm backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{stat.label}</p>
                  <p className="mt-2 text-base font-bold text-foreground">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-4">
              {onboardingHighlights.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex gap-4 rounded-3xl border border-border/70 bg-card/85 p-5 shadow-warm backdrop-blur">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl gradient-warm shadow-warm">
                      <Icon className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h2 className="font-display text-lg font-bold text-foreground">{item.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap gap-3 text-sm text-muted-foreground">
              <div className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2">
                <MapPin className="h-4 w-4 text-primary" />
                Campus rides for essentials
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2">
                <Clock3 className="h-4 w-4 text-primary" />
                Support for urgent needs
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2">
                <Wallet className="h-4 w-4 text-primary" />
                Distance-based fare model
              </div>
            </div>
          </section>

          <section className="w-full">
            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm-lg backdrop-blur sm:p-8">
              {step === 'role-select' && (
                <div className="space-y-6">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Create student account
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">Join CollegeRyde</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      Choose how you want to participate in the network. You can join as a rider looking for dependable access or as a driver helping classmates get where they need to go.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => handleRoleSelect('rider')}
                      className={cn(
                        'w-full rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-warm-lg',
                        'flex items-start gap-4'
                      )}
                    >
                      <div className="rounded-2xl bg-accent/15 p-3">
                        <GraduationCap className="h-6 w-6 text-accent" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="font-display text-lg font-bold text-foreground">Rider</h3>
                          <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">Book support</span>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          Find reliable student rides for classes, errands, airports, and other everyday campus mobility needs.
                        </p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleRoleSelect('driver')}
                      className={cn(
                        'w-full rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-warm-lg',
                        'flex items-start gap-4'
                      )}
                    >
                      <div className="rounded-2xl gradient-warm p-3 shadow-warm">
                        <Car className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="font-display text-lg font-bold text-foreground">Driver</h3>
                          <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">Offer rides</span>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          Share your route, help classmates move with confidence, and contribute to a stronger campus mobility system.
                        </p>
                      </div>
                    </button>
                  </div>

                  <div className="rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p className="text-sm leading-6 text-muted-foreground">
                      CollegeRyde is currently structured around verified student participation, community responsibility, and practical transportation access.
                    </p>
                  </div>

                  <button
                    onClick={onLoginClick}
                    className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Already have an account? <span className="font-medium text-primary">Log in</span>
                  </button>
                </div>
              )}

              {(step === 'details-form' || step === 'submitting') && selectedRole && (
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                        {selectedRole === 'driver' ? 'Driver enrollment' : 'Rider enrollment'}
                      </div>
                      <h2 className="mt-4 font-display text-3xl font-bold text-foreground">
                        Create your {selectedRole === 'driver' ? 'Driver' : 'Rider'} account
                      </h2>
                      <p className="mt-3 text-sm leading-6 text-muted-foreground">
                        Fill in your details to access the CollegeRyde student mobility network.
                      </p>
                    </div>
                    <div className="hidden rounded-3xl gradient-warm p-4 shadow-warm sm:block">
                      <SelectedRoleIcon className="h-7 w-7 text-primary-foreground" />
                    </div>
                  </div>

                  <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 pl-10 rounded-xl"
                    autoFocus
                    disabled={step === 'submitting'}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="student@university.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-10 rounded-xl"
                    disabled={step === 'submitting'}
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="h-12 pl-10 rounded-xl"
                    disabled={step === 'submitting'}
                  />
                </div>
              </div>

              {/* Driver-specific fields */}
              {selectedRole === 'driver' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Driver License Number *</label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="DL123456789"
                        value={driverLicense}
                        onChange={(e) => setDriverLicense(e.target.value)}
                        className="h-12 pl-10 rounded-xl"
                        disabled={step === 'submitting'}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">License Plate *</label>
                    <div className="relative">
                      <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="ABC 1234"
                        value={licensePlate}
                        onChange={(e) => setLicensePlate(e.target.value)}
                        className="h-12 pl-10 rounded-xl"
                        disabled={step === 'submitting'}
                      />
                    </div>
                  </div>
                  
                  {/* Security Note */}
                  <p className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                    🔒 Your driver license number & license plate number are encrypted with elliptic curve cryptography and stored securely per state regulations.
                  </p>
                </>
              )}

              <div className="rounded-2xl border border-border bg-card/60 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    id="accept-terms"
                    type="checkbox"
                    checked={hasAcceptedTerms}
                    onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                    disabled={step === 'submitting'}
                    className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="accept-terms" className="text-sm leading-6 text-foreground">
                    I have reviewed and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setIsTermsOpen(true)}
                      className="font-semibold text-primary underline-offset-4 hover:underline"
                      disabled={step === 'submitting'}
                    >
                      CollegeRyde Policy and Guideline
                    </button>
                    , including safety guidance, insurance responsibility, fare limits, and CollegeRyde&apos;s role as a student connection platform.
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => setIsTermsOpen(true)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  disabled={step === 'submitting'}
                >
                  <FileText className="h-4 w-4" />
                  Read full document before signing up
                </button>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={step === 'submitting' || !hasAcceptedTerms}
                className="w-full h-14 text-lg rounded-xl bg-primary hover:bg-primary/90"
              >
                {step === 'submitting' ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <button
                onClick={() => setStep('role-select')}
                className="w-full text-center text-muted-foreground hover:text-foreground transition-colors py-2 text-sm"
                disabled={step === 'submitting'}
              >
                ← Change role
              </button>
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-border/70 pt-5 text-center">
                <p className="text-xs leading-5 text-muted-foreground">
                  By creating an account, you agree to our CollegeRyde Policy and Guideline
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Dialog open={isTermsOpen} onOpenChange={setIsTermsOpen}>
        <DialogContent className="flex h-[92vh] w-[96vw] max-w-3xl flex-col overflow-hidden rounded-3xl border-border p-0 sm:h-auto sm:max-h-[90vh]">
          <DialogHeader className="border-b border-border bg-muted/30 px-4 py-4 pr-12 text-left sm:px-6 sm:py-5 sm:pr-14">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-xl">CollegeRyde Policy and Guideline</DialogTitle>
                <DialogDescription className="mt-2">
                  Page {termsPage + 1} of {termsPages.length}. Please review this document before creating your account.
                </DialogDescription>
              </div>
              <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                Student Safety First
              </div>
            </div>
            <div className="mt-4 rounded-2xl border border-primary/15 bg-primary/5 px-3 py-3 text-xs leading-5 text-muted-foreground sm:px-4">
              This document remains a tentative guideline and safety policy of CollegeRyde and is expected to be modified, updated, or changed as the platform grows.
            </div>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-6 sm:py-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">Terms Summary</p>
                <h3 className="mt-1 text-xl font-bold text-foreground sm:text-2xl">{currentTermsPage.title}</h3>
              </div>
              <div className="hidden rounded-2xl border border-border bg-card px-4 py-3 text-right sm:block">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Document Length</p>
                <p className="text-lg font-semibold text-foreground">5 pages</p>
              </div>
            </div>

            <div
              ref={termsContentRef}
              onScroll={handleTermsScroll}
              className="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-border bg-card/50"
            >
              <div className="space-y-5 p-5 sm:p-6">
                {currentTermsPage.body.map((paragraph) => (
                  <p key={paragraph} className="text-sm leading-7 text-foreground">
                    {paragraph}
                  </p>
                ))}

                {termsPage === termsPages.length - 1 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm leading-6 text-amber-950">
                    Students should use caution at all times. CollegeRyde helps classmates connect, but each rider and driver remains responsible for their choices, conduct, insurance, and personal safety on and off the road.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-border/70 bg-background pt-4 sm:mt-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                {hasReachedPageEnd
                  ? 'You have reached the end of this section and can continue.'
                  : 'Scroll to the end of this section to continue.'}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTermsPage((page) => Math.max(0, page - 1))}
                  disabled={termsPage === 0}
                  className="rounded-xl"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Previous
                </Button>
                <Button
                  type="button"
                  variant={termsPage === termsPages.length - 1 ? 'default' : 'outline'}
                  onClick={() => {
                    if (termsPage === termsPages.length - 1) {
                      setHasAcceptedTerms(true);
                      setIsTermsOpen(false);
                      return;
                    }
                    setTermsPage((page) => Math.min(termsPages.length - 1, page + 1));
                  }}
                  disabled={!hasReachedPageEnd}
                  className="rounded-xl"
                >
                  {termsPage === termsPages.length - 1 ? 'Accept and Continue' : 'Next page'}
                  {termsPage !== termsPages.length - 1 && <ChevronRight className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingFlow;
